import { type CSSProperties, type KeyboardEvent, type PointerEvent, type RefObject } from 'react'
import { RefreshCw } from 'lucide-react'
import { LmsButton } from '@shared/ui'
import type { ProtectedReaderSession } from './flipbook-contracts'
import { FLIPBOOK_PAGE_TURN_DURATION_MS } from './flipbook-constants'
import { FlipbookAnnotationLayer, type FlipbookAnnotationTool } from './FlipbookAnnotationLayer'
import type { FlipbookAnnotationHistoryControls, FlipbookAnnotationState } from './flipbook-annotation-contracts'
import type { TurningPage } from './useFlipbookNavigation'
import type { PanPosition } from './useFlipbookViewport'
import { getFlipbookPerformanceDiagnostics, useFlipbookPerformanceRenderCount } from './flipbook-performance-diagnostics'
import './flipbook.css'

interface FlipbookStageProps {
  readonly bookRef: RefObject<HTMLDivElement | null>
  readonly displayPage: number
  readonly errorMessage: string | null
  readonly annotationTool: FlipbookAnnotationTool
  readonly highlighterColor: string
  readonly hydratedAnnotationState: FlipbookAnnotationState | null
  readonly getRenderedPageImage: (page: number) => string | undefined
  readonly getRenderedPageNumber: (page: number) => number
  readonly hasVisiblePage: boolean
  readonly isFullscreen: boolean
  readonly isPageLoading: boolean
  readonly isPanning: boolean
  readonly isSpreadView: boolean
  readonly isAnnotationHydrationReady: boolean
  readonly annotationHydrationKey: string
  readonly onAnnotationHistoryChange: (controls: FlipbookAnnotationHistoryControls) => void
  readonly onAnnotationStateChange: (state: FlipbookAnnotationState) => void
  readonly onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void
  readonly onPageImageError: () => void
  readonly onPointerCancel: (event: PointerEvent<HTMLDivElement>) => void
  readonly onPointerDown: (event: PointerEvent<HTMLDivElement>) => void
  readonly onPointerMove: (event: PointerEvent<HTMLDivElement>) => void
  readonly onPointerUp: (event: PointerEvent<HTMLDivElement>) => void
  readonly onRetry: () => void
  readonly pageAspectRatio: number
  readonly pan: PanPosition
  readonly penColor: string
  readonly session: ProtectedReaderSession
  readonly slotLabel: string
  readonly stageRef: RefObject<HTMLDivElement | null>
  readonly turningPage: TurningPage | null
  readonly textColor: string
  readonly visiblePageNumbers: readonly number[]
  readonly zoom: number
}

export function FlipbookStage({
  bookRef,
  displayPage,
  errorMessage,
  annotationTool,
  highlighterColor,
  hydratedAnnotationState,
  getRenderedPageImage,
  getRenderedPageNumber,
  hasVisiblePage,
  isFullscreen,
  isPageLoading,
  isPanning,
  isSpreadView,
  isAnnotationHydrationReady,
  annotationHydrationKey,
  onAnnotationHistoryChange,
  onAnnotationStateChange,
  onKeyDown,
  onPageImageError,
  onPointerCancel,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onRetry,
  pageAspectRatio,
  pan,
  penColor,
  session,
  slotLabel,
  stageRef,
  turningPage,
  textColor,
  visiblePageNumbers,
  zoom,
}: FlipbookStageProps) {
  useFlipbookPerformanceRenderCount('stage')

  return (
    <div
      ref={stageRef}
      aria-label={`Protected ${slotLabel} PDF pages ${displayPage}${visiblePageNumbers.length > 1 ? ` and ${displayPage + 1}` : ''} of ${session.pageCount}`}
      className="pdfFlipbookViewport"
      onContextMenu={(event) => { event.preventDefault() }}
      onKeyDown={onKeyDown}
      onPointerCancel={onPointerCancel}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      role="region"
      style={{
        minHeight: isFullscreen ? 'calc(100dvh - 4rem)' : undefined,
        touchAction: zoom > 1 ? 'none' : 'pan-y',
      }}
      tabIndex={0}
    >
      <div
        ref={bookRef}
        className={`pdfFlipbookBook${isFullscreen ? ' isFullscreen' : ''}`}
        style={{
          aspectRatio: isSpreadView ? `${pageAspectRatio * 2}` : `${pageAspectRatio}`,
          height: isFullscreen ? 'min(calc(100dvh - 5rem), 56rem)' : 'min(78vh, 52rem)',
          cursor: zoom > 1 ? (isPanning ? 'grabbing' : 'grab') : 'default',
          transform: `translate3d(${pan.x}px, ${pan.y}px, 0) scale(${zoom})`,
          transition: isPanning ? 'none' : 'transform 180ms ease-out',
        }}
      >
        {visiblePageNumbers.map((page, index) => (
          <div
            className={`pdfFlipbookPage ${index === 0 ? 'pdfFlipbookPage--left' : 'pdfFlipbookPage--right'}`}
            key={page}
            style={{
              borderRadius: index === 0 ? '.3rem 0 0 .3rem' : '0 .3rem .3rem 0',
              width: isSpreadView ? '50%' : '100%',
            }}
          >
            {getRenderedPageImage(page) ? (
              <img
                alt={`Page ${getRenderedPageNumber(page)} of ${session.pageCount}`}
                draggable={false}
                onLoad={(event) => {
                  const image = event.currentTarget
                  void image.decode().catch(() => undefined).finally(() => {
                    const renderedPageNumber = getRenderedPageNumber(page)
                    const diagnostics = getFlipbookPerformanceDiagnostics()
                    diagnostics.markFirstOpenImageDecoded()
                    if (diagnostics.markImageLoaded(renderedPageNumber)) {
                      window.requestAnimationFrame(() => {
                        diagnostics.markFirstOpenVisibleCommit()
                        diagnostics.markPageTurnVisibleFirstRaf(renderedPageNumber)
                      })
                    }
                  })
                }}
                onError={onPageImageError}
                src={getRenderedPageImage(page)}
              />
            ) : null}
            <div
              aria-hidden="true"
              className="pdfFlipbookPageGradient"
              style={{
                background: isSpreadView
                  ? index === 0
                    ? 'linear-gradient(90deg, rgb(0 0 0 / 0%) 95%, rgb(0 0 0 / 15%) 100%)'
                    : 'linear-gradient(270deg, rgb(0 0 0 / 0%) 95%, rgb(0 0 0 / 15%) 100%)'
                  : 'linear-gradient(90deg, rgb(0 0 0 / 15%) 0%, rgb(0 0 0 / 0%) 5%)',
              }}
            />
            <span className="pdfFlipbookPageNumber">
              {getRenderedPageNumber(page)}
            </span>
          </div>
        ))}
        <FlipbookAnnotationLayer
          activeTool={annotationTool}
          disabled={isPageLoading || Boolean(turningPage)}
          highlighterColor={highlighterColor}
          hydratedState={hydratedAnnotationState}
          isHydrationReady={isAnnotationHydrationReady}
          isSpreadView={isSpreadView}
          hydrationKey={annotationHydrationKey}
          onHistoryChange={onAnnotationHistoryChange}
          onStateChange={onAnnotationStateChange}
          penColor={penColor}
          textColor={textColor}
          visiblePageNumbers={visiblePageNumbers}
        />
        {turningPage ? (
          <>
            <span
              aria-hidden="true"
              className={`pdfFlipbookCastShadow pdfFlipbookCastShadow--${turningPage.direction}${isSpreadView ? '' : ' isSingle'}`}
              style={{ '--pdf-flipbook-turn-duration': `${FLIPBOOK_PAGE_TURN_DURATION_MS}ms` } as CSSProperties}
            />
            <div
              aria-hidden="true"
              className={`pdfFlipbookTurnSheet pdfFlipbookTurnSheet--${turningPage.direction}`}
              style={{
                '--pdf-flipbook-turn-duration': `${FLIPBOOK_PAGE_TURN_DURATION_MS}ms`,
                borderRadius: turningPage.direction === 'next' ? '0 .3rem .3rem 0' : '.3rem 0 0 .3rem',
                left: turningPage.direction === 'previous' || !isSpreadView ? 0 : '50%',
                right: turningPage.direction === 'next' || !isSpreadView ? 0 : '50%',
                transformOrigin: turningPage.direction === 'next' ? 'left center' : 'right center',
                width: isSpreadView ? '50%' : '100%',
              } as CSSProperties}
            >
              <div className="pdfFlipbookTurnSheetFace pdfFlipbookTurnSheetFace--front">
                <img alt="" draggable={false} src={turningPage.url} />
                <span className="pdfFlipbookPageNumber">
                  {turningPage.page}
                </span>
              </div>
              <div className="pdfFlipbookTurnSheetFace pdfFlipbookTurnSheetFace--back">
                {turningPage.backUrl ? <img alt="" draggable={false} src={turningPage.backUrl} /> : null}
              </div>
              <span className="pdfFlipbookTurnSheetCurl" />
              <span className="pdfFlipbookTurnSheetRidge" />
              <span className="pdfFlipbookTurnSheetShade" />
            </div>
          </>
        ) : null}
      </div>

      {isPageLoading && !hasVisiblePage ? (
        <div className="pdfFlipbookLoading" role="status">
          <span aria-hidden="true" className="pdfFlipbookSpinner" />
          <span>Loading pages {displayPage}…</span>
        </div>
      ) : null}

      {!isPageLoading && !hasVisiblePage && errorMessage ? (
        <div className="pdfFlipbookErrorOverlay">
          <p role="alert">
            {errorMessage}
          </p>
          <LmsButton className="min-h-11" leadingIcon={<RefreshCw aria-hidden="true" className="size-4" />} onClick={onRetry} variant="outline">
            Renew reader
          </LmsButton>
        </div>
      ) : null}

    </div>
  )
}
